"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { DataItem } from "@/types";

export type Column = {
  id: string;
  header: React.ReactNode;
  accessorKey: string;
  className?: string;
  isNumeric?: boolean;
  summaryAccessorKey?: string;
  summaryFormatter?: (value: number) => React.ReactNode;
  hideOnBreakpoint?: string; // E.g. 'md' to hide on medium screens and below
};

export type ActionButton = {
  id: string;
  icon: React.ReactNode;
  label?: string;
  onClick?: (row: DataItem, index: number) => void;
  className?: string;
};

export type TableProps = {
  columns: Column[];
  data: DataItem[];
  expandedContentKey?: string;
  actions?: ActionButton[];
  actionKey?: string;
  className?: string;
  rowClassName?: string;
  headerClassName?: string;
  cellClassName?: string;
  actionsClassName?: string;
  showSummary?: boolean;
  summaryText?: string;
};

export default function Table({
  columns,
  data,
  expandedContentKey,
  actions = [],
  actionKey,
  className,
  rowClassName,
  headerClassName,
  cellClassName,
  actionsClassName,
  showSummary = false,
  summaryText = "Total",
}: TableProps) {
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({});
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  // Update window width on resize
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine which columns to show based on screen size
  const visibleColumns = useMemo(() => {
    return columns.filter((column) => {
      if (!column.hideOnBreakpoint) return true;

      // Define breakpoints (match these with your Tailwind breakpoints)
      const breakpoints = {
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        "2xl": 1536,
      };

      const breakpoint =
        breakpoints[column.hideOnBreakpoint as keyof typeof breakpoints];
      return windowWidth >= breakpoint;
    });
  }, [columns, windowWidth]);

  const toggleRow = (index: number) => {
    setOpenRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const hasExpandedContent = !!expandedContentKey;
  const hasActions = actions.length > 0 || !!actionKey;

  // Find the maximum number of action buttons in any row
  const getMaxActionButtonCount = () => {
    let max = actions.length;
    if (actionKey) {
      data.forEach((row) => {
        if (row[actionKey] && Array.isArray(row[actionKey])) {
          max = Math.max(max, actions.length + (row[actionKey] as []).length);
        }
      });
    }
    return max;
  };

  const maxActionButtons = getMaxActionButtonCount();

  // Calculate precise action column width based on button count
  const actionColumnWidth =
    maxActionButtons > 0 ? `${maxActionButtons * 40}px` : "auto";

  // Calculate column summaries for numeric columns
  const summaries = useMemo(() => {
    if (!showSummary) return null;

    const result: Record<string, number> = {};

    columns.forEach((column) => {
      if (column.isNumeric) {
        // Use summaryAccessorKey if provided, otherwise use the regular accessorKey for calculation
        const calcKey = column.summaryAccessorKey || column.accessorKey;

        result[column.accessorKey] = data.reduce((sum, row) => {
          const value = parseFloat(String(row[calcKey]));
          return !isNaN(value) ? sum + value : sum;
        }, 0);
      }
    });

    return result;
  }, [columns, data, showSummary]);

  return (
    <div className={cn("w-full", className)}>
      {/* Header */}
      <div className={cn("flex items-center border-b py-3", headerClassName)}>
        {visibleColumns.map((column, colIndex) => (
          <div
            key={column.id}
            className={cn(
              "flex-1",
              colIndex === 0 ? "pl-2" : "",
              column.className
            )}
          >
            {column.header}
          </div>
        ))}
        {hasActions && (
          <div
            className={cn(
              "flex justify-end items-center pl-2",
              actionsClassName
            )}
            style={{ width: actionColumnWidth }}
          />
        )}
        {hasExpandedContent && <div className='w-10 flex-shrink-0' />}
      </div>

      {/* Rows */}
      {data.map((row, rowIndex) => (
        <Collapsible
          key={rowIndex}
          open={openRows[rowIndex]}
          onOpenChange={() => hasExpandedContent && toggleRow(rowIndex)}
          className={cn("border-b last:border-b-0", rowClassName)}
        >
          <div
            className={cn(
              "flex items-center py-3 hover:bg-muted/50 transition-colors",
              hasExpandedContent &&
                expandedContentKey &&
                Boolean(row[expandedContentKey]) &&
                "cursor-pointer"
            )}
            onClick={() =>
              hasExpandedContent &&
              expandedContentKey &&
              row[expandedContentKey] &&
              toggleRow(rowIndex)
            }
          >
            {visibleColumns.map((column, colIndex) => (
              <div
                key={column.id}
                className={cn(
                  "flex-1 truncate",
                  colIndex === 0 ? "pl-2" : "",
                  column.isNumeric ? "text-right" : "",
                  cellClassName,
                  column.className
                )}
              >
                {row[column.accessorKey] as React.ReactNode}
              </div>
            ))}

            {/* Action Buttons */}
            {hasActions && (
              <div
                className={cn(
                  "flex justify-end items-center gap-1 pl-2",
                  actionsClassName
                )}
                style={{ width: actionColumnWidth }}
              >
                {/* Global actions defined in props */}
                {actions.map((action) => (
                  <button
                    key={action.id}
                    className={cn(
                      "h-8 w-8 p-1 rounded-sm hover:bg-muted/80 flex items-center justify-center",
                      action.className
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick?.(row, rowIndex);
                    }}
                    aria-label={action.label}
                    title={action.label}
                  >
                    {action.icon}
                  </button>
                ))}

                {actionKey &&
                  Boolean(row[actionKey]) &&
                  Array.isArray(row[actionKey]) &&
                  (row[actionKey] as ActionButton[]).map(
                    (action: ActionButton, i: number) => (
                      <button
                        key={`${action.id || i}`}
                        className={cn(
                          "h-8 w-8 p-1 rounded-sm hover:bg-muted/80 flex items-center justify-center",
                          action.className
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick?.(row, rowIndex);
                        }}
                        aria-label={action.label}
                        title={action.label}
                      >
                        {action.icon}
                      </button>
                    )
                  )}

                {/* Add empty placeholders to maintain alignment if this row has fewer buttons than the max */}
                {actionKey &&
                  Boolean(row[actionKey]) &&
                  Array.isArray(row[actionKey]) &&
                  [
                    ...Array(
                      maxActionButtons -
                        (actions.length + (row[actionKey] as []).length)
                    ),
                  ].map((_, i) => <div key={i} className='w-8 h-8' />)}

                {/* Add empty placeholders if row has no custom actions */}
                {(!actionKey ||
                  !Boolean(row[actionKey]) ||
                  !Array.isArray(row[actionKey])) &&
                  [...Array(maxActionButtons - actions.length)].map((_, i) => (
                    <div key={i} className='w-8 h-8' />
                  ))}
              </div>
            )}

            {/* Expand/Collapse Button */}
            {hasExpandedContent && (
              <div className='flex justify-center w-10 flex-shrink-0'>
                {expandedContentKey && Boolean(row[expandedContentKey]) && (
                  <CollapsibleTrigger
                    asChild
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className='h-8 w-8 p-1 rounded-sm hover:bg-muted/80 flex items-center justify-center'>
                      {openRows[rowIndex] ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </button>
                  </CollapsibleTrigger>
                )}
              </div>
            )}
          </div>

          {hasExpandedContent &&
            expandedContentKey &&
            Boolean(row[expandedContentKey]) && (
              <CollapsibleContent>
                <div className='py-3 bg-muted/30'>
                  {/* Handle both array and single object for expandedContentKey */}
                  {Array.isArray(row[expandedContentKey]) ? (
                    /* If it's an array, map over it */
                    (row[expandedContentKey] as DataItem[]).map(
                      (item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className='flex items-center mb-2 last:mb-0'
                        >
                          {visibleColumns.map((column, colIndex) => (
                            <div
                              key={column.id}
                              className={cn(
                                "flex-1 truncate",
                                colIndex === 0 ? "pl-2" : "",
                                column.isNumeric ? "text-right" : "",
                                cellClassName,
                                column.className
                              )}
                            >
                              {item && item[column.accessorKey]
                                ? (item[column.accessorKey] as React.ReactNode)
                                : ""}
                            </div>
                          ))}

                          {/* Empty action area to maintain alignment */}
                          {hasActions && (
                            <div
                              className={cn(
                                "flex justify-end items-center gap-1 pl-2",
                                actionsClassName
                              )}
                              style={{ width: actionColumnWidth }}
                            />
                          )}

                          {/* Empty space for alignment */}
                          {hasExpandedContent && (
                            <div className='w-10 flex-shrink-0' />
                          )}
                        </div>
                      )
                    )
                  ) : (
                    /* If it's a single object, render it as before */
                    <div className='flex items-center'>
                      {visibleColumns.map((column, colIndex) => {
                        const nestedData = row[expandedContentKey] as DataItem;
                        return (
                          <div
                            key={column.id}
                            className={cn(
                              "flex-1 truncate",
                              colIndex === 0 ? "pl-2" : "",
                              column.isNumeric ? "text-right" : "",
                              cellClassName,
                              column.className
                            )}
                          >
                            {nestedData && nestedData[column.accessorKey]
                              ? (nestedData[
                                  column.accessorKey
                                ] as React.ReactNode)
                              : ""}
                          </div>
                        );
                      })}

                      {/* Empty action area to maintain alignment */}
                      {hasActions && (
                        <div
                          className={cn(
                            "flex justify-end items-center gap-1 pl-2",
                            actionsClassName
                          )}
                          style={{ width: actionColumnWidth }}
                        />
                      )}

                      {/* Empty space for alignment */}
                      {hasExpandedContent && (
                        <div className='w-10 flex-shrink-0' />
                      )}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            )}
        </Collapsible>
      ))}

      {/* Summary Row */}
      {showSummary && summaries && (
        <div
          className={cn(
            "flex items-center py-3 border-t-2  border-t-primary font-medium",
            rowClassName
          )}
        >
          {visibleColumns.map((column, colIndex) => (
            <div
              key={column.id}
              className={cn(
                "flex-1 truncate",
                colIndex === 0 ? "pl-2" : "",
                column.isNumeric ? "text-right" : "",
                cellClassName,
                column.className
              )}
            >
              {colIndex === 0
                ? summaryText
                : column.isNumeric &&
                    summaries[column.accessorKey] !== undefined
                  ? column.summaryFormatter
                    ? column.summaryFormatter(summaries[column.accessorKey])
                    : summaries[column.accessorKey]
                  : ""}
            </div>
          ))}

          {/* Empty action area to maintain alignment */}
          {hasActions && (
            <div
              className={cn(
                "flex justify-end items-center gap-1 pl-2",
                actionsClassName
              )}
              style={{ width: actionColumnWidth }}
            />
          )}

          {/* Empty space for alignment */}
          {hasExpandedContent && <div className='w-10 flex-shrink-0' />}
        </div>
      )}
    </div>
  );
}
